import { SubChecklistCard } from 'types/electionService';
import { getGroupById } from 'api/operations/groupService';
import { SubCardTextObject } from 'global/checklists-helpers/SubscriberChecklistHelper';
import { makeAutoObservable, runInAction } from 'mobx';
import { createContext, useContext } from 'react';
import { bpHistory } from 'global/bpHistory';
import { ChecklistOption } from 'types/enrollmentService';
import { Group } from 'types/groupService';
import { getSubscriberById } from 'api/operations/electionService';
import SubscriberDelegate from 'store/modules/elections/subscriberDelegate';

interface DomainParams {
  groupId: string;
  subscriberId: string;
  // TaskId
  entityId: string;
}

export class SubscriberTaskChecklistPageStore {
  private _isLoading: boolean = false;
  private _domainParams: DomainParams;
  private _subscriberDelegate: SubscriberDelegate | null = null;
  private _group: Pick<Group, 'name' | 'id' | 'contactInfo'> | null = null;

  public get isLoading(): boolean {
    return this._isLoading;
  }

  public get checklistOptions(): ChecklistOption<SubChecklistCard>[] {
    if (!this._subscriberDelegate) return [];
    return this._subscriberDelegate.getTaskChecklistOptions(this._domainParams.entityId);
  }

  public get domainParams(): DomainParams {
    return this._domainParams;
  }

  public get group(): Pick<Group, 'name' | 'id' | 'contactInfo'> | null {
    return this._group;
  }

  private _setIsLoading(isLoading: boolean): void {
    this._isLoading = isLoading;
  }

  private _setGroup(group: Group): void {
    this._group = {
      name: group.name,
      id: group.id,
      contactInfo: group.contactInfo,
    };
  }

  private async _getSubscriber(): Promise<void> {
    const subscriber = await getSubscriberById(this._domainParams.subscriberId);
    runInAction(() => {
      this._subscriberDelegate = new SubscriberDelegate({ subscriber, periodId: this._domainParams.entityId });
    });
  }

  public async getInitialData(): Promise<void> {
    this._setIsLoading(true);
    const request = Promise.all([getGroupById(this._domainParams.groupId), this._getSubscriber()]);
    await runInAction(async () => {
      const [group] = await request;
      this._setGroup(group);
    });
    this._setIsLoading(false);
  }

  public returnBack(): void {
    bpHistory.push(`/employee?groupId=${this._domainParams.groupId}`);
  }

  public onStepRedirect(cardData: SubCardTextObject): void {
    if (cardData.redirectTo) {
      const url = cardData.redirectTo({
        groupId: this._domainParams.groupId,
        entityId: this._domainParams.entityId,
        subId: this._domainParams.subscriberId,
      });
      bpHistory.push(url);
    }
  }

  constructor(params: DomainParams) {
    this._domainParams = params;

    makeAutoObservable(this, {}, { autoBind: true });
  }
}

const SubscriberTaskChecklistPageStoreContext = createContext(
  new SubscriberTaskChecklistPageStore({
    groupId: 'stub-group-id',
    subscriberId: 'stub-subscriber-id',
    entityId: 'stub-entity-id',
  }),
);

export const useSubscriberTaskChecklistPageStore = (): SubscriberTaskChecklistPageStore => {
  return useContext(SubscriberTaskChecklistPageStoreContext);
};

export { SubscriberTaskChecklistPageStoreContext };
